import type { Phrases } from '../common/Phrases.d';
import type {
	ColorName,
	PillShape,
	SubstanceForm,
} from '../common/constants';
import type { IntoxicantOption } from '../../../../site/parts/notificationFormAnonymous/IntoxicantOption.d';

import {
	faArrowsMaximize,
	faDiceD8,
	faFlaskPotion,
	faMortarPestle,
	faNose,
	faPills,
} from '@fortawesome/pro-light-svg-icons';
import {
	faCapsules,
	faCircleCheck,
	faLightbulbOn,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle as faCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import React, { type FC, useState } from 'react';
import { Figure, Img, cn } from '../../../components/Element';
import { Ul, Li } from '../../../components/List';
import { H1, H2, H3 } from '../../../components/heading/Heading';
import { Flex, Row, Stack } from '../../../components/Flex';
import { FontAwesomeIconWrapper as Icon } from '../../../components/FontAwesomeIconWrapper';
import { FakeButton } from '../../../components/FakeButton';
import {
	CSS_COLOR_CLASS,
	COLOR_NAME,
	PILL_SHAPE,
	SUBSTANCE,
	SUBSTANCE_FORM,
} from '../common/constants';
import { PILL_SHAPE_ICONS } from '../common/Icons';
import { getIntoxicantDescriptionLabel } from '../common/IntoxicantDescriptionTextField';
import { sanitizeHtml } from '../../../shared/sanitizeHtml';
import { Lightbox } from '../../../shared/Lightbox/Lightbox';


export const SuccessPageHeader1 = ({
	h1Ref,
	phrases,
}: {
	h1Ref: React.MutableRefObject<HTMLHeadingElement | undefined>;
	phrases: Phrases;
}): React.JSX.Element => (
	<H1 elementRef={h1Ref} tabIndex={-1}>
		<Icon icon={faCircleCheck} className={cn({color: 'surfaceStatistic8'})}/>
		&nbsp;
		{phrases['notificationForm.common.successPage.heading1']}
	</H1>
);


export const SuccessPageInfobox = ({
	phrases,
}: {
	phrases: Phrases;
}): React.JSX.Element => (
	<Stack bgColor='surfaceActionChip10Default' className='gap-md' padding={{all: 'xl'}}>
		<H2><Icon icon={faLightbulbOn}/> {phrases['notificationForm.common.successPage.heading2']}</H2>
		<Ul className='list-inside pl-xl'>
			<Li
				dangerouslySetInnerHTML={{ __html: sanitizeHtml(phrases[
					'notificationForm.common.successPage.li1.html'
				])}}
			/>
			<Li
				dangerouslySetInnerHTML={{ __html: sanitizeHtml(phrases[
					'notificationForm.common.successPage.li2.html'
				])}}
			/>
			<Li
				dangerouslySetInnerHTML={{ __html: sanitizeHtml(phrases[
					'notificationForm.common.successPage.li3.html'
				])}}
			/>
			<Li
				dangerouslySetInnerHTML={{ __html: sanitizeHtml(phrases[
					'notificationForm.common.successPage.li4.html'
				])}}
			/>
		</Ul>
	</Stack>
);


const MIXTURE_AND_SUBSTANCE: string[] = [SUBSTANCE.MIXTURE,SUBSTANCE.OTHER];

export const SuccessPageSubstance = ({
	intoxicantOptions,
	notFilledOut,
	phrases,
	substance,
	substanceOther,
}: {
	intoxicantOptions: IntoxicantOption[];
	notFilledOut: string;
	phrases: Phrases;
	substance: string; // Now comes from Content.
	substanceOther?: string;
}): React.JSX.Element => (
	<Stack className='gap-md'>
		<H3>{phrases['notificationForm.common.substanceField.label']}</H3>
		<p>{`${substance && intoxicantOptions.filter(({id}) => id === substance)?.[0]?.label || ''}${
			substanceOther ? `: ${substanceOther}`
				: MIXTURE_AND_SUBSTANCE.includes(substance) ? ` ${notFilledOut}`
					: ''
		}`}</p>
	</Stack>
);

function getTranslatedTextFromSubstanceForm({
	phrases,
	substanceForm,
}: {
	phrases: Phrases;
	substanceForm?: SubstanceForm;
}): string {
	return phrases[
		substanceForm === SUBSTANCE_FORM.PILL ? 'notificationForm.common.formField.value.pill'
			: substanceForm === SUBSTANCE_FORM.CAPSULE ? 'notificationForm.common.formField.value.capsule'
				: substanceForm === SUBSTANCE_FORM.NASAL_SPRAY ? 'notificationForm.common.formField.value.nasalSpray'
					: substanceForm === SUBSTANCE_FORM.CRYSTAL ? 'notificationForm.common.formField.value.crystal'
						: substanceForm === SUBSTANCE_FORM.POWDER ? 'notificationForm.common.formField.value.powder'
							: substanceForm === SUBSTANCE_FORM.LIQUID ? 'notificationForm.common.formField.value.liquid'
								: 'notificationForm.common.formField.value.other'
	];
}

export const SuccessPageSubstanceForm = ({
	phrases,
	substanceForm,
}: {
	phrases: Phrases,
	substanceForm?: SubstanceForm;
}): React.JSX.Element => (
	<Stack className='gap-md'>
		<H3>{phrases['notificationForm.common.formField.label']}</H3>
		<Row className='gap-md items-center'>
			<FakeButton>
				{
					substanceForm === SUBSTANCE_FORM.OTHER
						? null
						: <Icon className='scale-125'
							icon={
								substanceForm === SUBSTANCE_FORM.PILL ? faPills
									: substanceForm === SUBSTANCE_FORM.CAPSULE ? faCapsules
										: substanceForm === SUBSTANCE_FORM.NASAL_SPRAY ? faNose
											: substanceForm === SUBSTANCE_FORM.CRYSTAL ? faDiceD8
												: substanceForm === SUBSTANCE_FORM.POWDER ? faMortarPestle
													: /* substanceForm === SUBSTANCE_FORM.LIQUID ? */ faFlaskPotion
							}
						/>
				}
				{
					getTranslatedTextFromSubstanceForm({ phrases, substanceForm })
				}
			</FakeButton>
		</Row>
	</Stack>
);

function getTranslatedTextFromPillShape({
	phrases,
	pillShape,
}: {
	phrases: Phrases;
	pillShape?: PillShape;
}): string {
	return pillShape === PILL_SHAPE.CIRCLE ? phrases['notificationForm.common.pillShapeField.value.circle'] :
		pillShape === PILL_SHAPE.ROUNDED_RECTANGLE ? phrases['notificationForm.common.pillShapeField.value.roundedRectangle'] :
			pillShape === PILL_SHAPE.SQUARE ? phrases['notificationForm.common.pillShapeField.value.square'] :
				pillShape === PILL_SHAPE.DIAMOND ? phrases['notificationForm.common.pillShapeField.value.diamond'] :
					pillShape === PILL_SHAPE.TRIANGLE ? phrases['notificationForm.common.pillShapeField.value.triangle'] :
						phrases['notificationForm.common.pillShapeField.value.other'];
}

export const SuccessPagePillShape = ({
	notFilledOut,
	phrases,
	pillShape,
	pillShapeOther,
}: {
	notFilledOut: string;
	phrases: Phrases;
	pillShape?: PillShape;
	pillShapeOther?: string;
}): React.JSX.Element | null => pillShape
	? <Stack className='gap-md'>
		<H3>{phrases['notificationForm.common.pillShapeField.label']}</H3>
		<Row className='gap-md items-center'>
			<FakeButton>
				{
					PILL_SHAPE_ICONS[pillShape]
						? <Icon icon={PILL_SHAPE_ICONS[pillShape]}/>
						: null
				}
				{
					getTranslatedTextFromPillShape({ phrases, pillShape })
				}
			</FakeButton>
			{

				pillShapeOther
					? pillShapeOther
					: pillShape === PILL_SHAPE.OTHER
						? ` ${notFilledOut}`
						: null
			}
		</Row>
	</Stack>
	: null;


export const SuccessPageColors = ({
	colorOther,
	colors,
	notFilledOut,
	phrases,
	substanceForm,
	translatedColors,
}: {
	colorOther?: string;
	colors?: ColorName[];
	notFilledOut: string;
	phrases: Phrases;
	substanceForm?: SubstanceForm;
	translatedColors: Record<ColorName, string>;
}): React.JSX.Element | null => (colors && colors.length)
	? <Stack className='gap-md'>
		<H3>
			{
				phrases[
					substanceForm === SUBSTANCE_FORM.PILL ? 'notificationForm.common.colorField.label.pill'
						: substanceForm === SUBSTANCE_FORM.CAPSULE ? 'notificationForm.common.colorField.label.capsuleContainer'
							: substanceForm === SUBSTANCE_FORM.CRYSTAL ? 'notificationForm.common.colorField.label.crystal'
								: substanceForm === SUBSTANCE_FORM.POWDER ? 'notificationForm.common.colorField.label.powder'
									: 'notificationForm.common.colorField.label.liquid'
				]
			}
		</H3>
		<Row className='gap-md items-center'>
			{
				colors.map((color, i) => {
					return (
						<FakeButton key={`color-${i}`}>
							{
								color !== COLOR_NAME.OTHER
									? <Icon className={CSS_COLOR_CLASS[color]} icon={faCircleSolid}/>
									: null
							}
							{translatedColors[color]}
						</FakeButton>
					);
				})
			}
			{
				colorOther
					? colorOther
					: colors.includes(COLOR_NAME.OTHER)
						? ` ${notFilledOut}`
						: null
			}
		</Row>
	</Stack>
	: null;


export const SuccessPageCapsulePowderColors = ({
	capsulePowderColors,
	capsulePowderColorOther,
	notFilledOut,
	phrases,
	translatedColors,
}: {
	capsulePowderColors?: ColorName[];
	capsulePowderColorOther?: string;
	notFilledOut: string;
	phrases: Phrases;
	translatedColors: Record<ColorName, string>;
}): React.JSX.Element | null => (capsulePowderColors && capsulePowderColors.length)
	? <Stack className='gap-md'>
		<H3>
			{phrases['notificationForm.common.capsulePowderColorField.label']}
		</H3>
		<Row className='gap-md items-center'>
			{
				capsulePowderColors.map((capsulePowderColor, i) => {
					return (
						<FakeButton key={`capsulePowderColor-${i}`}>
							{
								capsulePowderColor !== COLOR_NAME.OTHER
									? <Icon className={CSS_COLOR_CLASS[capsulePowderColor]} icon={faCircleSolid}/>
									: null
							}
							{translatedColors[capsulePowderColor]}
						</FakeButton>
					);
				})
			}
			{
				capsulePowderColorOther
					? capsulePowderColorOther
					: capsulePowderColors.includes(COLOR_NAME.OTHER)
						? ` ${notFilledOut}`
						: null
			}
		</Row>
	</Stack>
	: null;


export const SuccessPageIntoxicantDescription = ({
	intoxicantDescription,
	notFilledOut,
	phrases,
	substanceForm,
}: {
	intoxicantDescription?: string;
	notFilledOut: string;
	phrases: Phrases;
	substanceForm?: SubstanceForm;
}): React.JSX.Element | null => substanceForm
	? <Stack className='gap-md'>
		<H3>{getIntoxicantDescriptionLabel({
			phrases,
			substanceForm,
		})}</H3>
		{intoxicantDescription ? intoxicantDescription : notFilledOut}
	</Stack>
	: null;


export interface SuccessPageImagesProps {
	imageDataUrls?: string[];
	phrases: Phrases;
}

const ImageAndLightbox = ({
	src,
}: {
	src: string;
}): React.JSX.Element => {
	const [lightboxImage, setLightboxImage] = useState<string | null>(null);
	const openLightboxWithKeys = (e: React.KeyboardEvent, callback: () => void): void => {
		if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
			e.preventDefault(); // Prevent the default action
			e.stopPropagation(); // Stop the event from bubbling up
			callback(); // Execute the callback function
		}
	};
	return (
		<div>
			<Figure
				className={[
					'cursor-pointer',
					'focus:opacity-75',
					'group',
					'hover:opacity-75',
					'inline-block',
					'relative',
				]}
				onClick={() => setLightboxImage(src)}
				onKeyDown={(e) => openLightboxWithKeys(e, () => setLightboxImage(src))}
				tabIndex={0}
				role='button'
				aria-label='Åpne bildevisning'
			>
				<Img
					alt='Bilde av rusmiddel'
					loading='eager'
					draggable={false}
					src={src}
				/>
				<Flex
					bgColor='surfaceActionPrimaryDefault'
					className={[
						'absolute',
						'duration-200',
						'ease-in-out',
						'group-focus:scale-[1.2]',
						'group-hover:scale-[1.2]',
						'items-center',
						'justify-center',
						'right-[8px]',
						'rounded-md',
						'top-[8px]',
						'transition-transform',
					]}
					padding={{
						all: 'xs',
					}}
				>
					<Icon
						className='text-white'
						fixedWidth={true}
						icon={faArrowsMaximize}
					/>
				</Flex>
			</Figure>
			<Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
		</div>
	);
};

export const SuccessPageImages: FC<SuccessPageImagesProps> = ({
	imageDataUrls,
	phrases,
}) => imageDataUrls && imageDataUrls.length
	? <Stack className='gap-md'>
		<H3>
			{phrases['notificationForm.common.imageField.label']}
		</H3>
		<div className='grid grid-cols-1 tablet:grid-cols-2 gap-md'>
			{
				imageDataUrls.map((src, i) => <ImageAndLightbox
					key={`image-${i}`}
					src={src}
				/>)
			}
		</div>
	</Stack>
	: null;


export const SuccessPageAdditionalInformation = ({
	additionalInformation,
	notFilledOut,
	phrases,
}: {
	additionalInformation?: string;
	notFilledOut: string;
	phrases: Phrases;
}): React.JSX.Element => (
	<Stack className='gap-md'>
		<H3>
			{phrases['notificationForm.common.additionalInformationField.label']}
		</H3>
		{additionalInformation || notFilledOut}
	</Stack>
);
